// src/pages/packingBatch/PrintWaybillsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Printer,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { getPackingBatchPrintData, markBatchAsPrinted } from '../../services/api/packingBatch';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import Label102x76Format1 from './PrintFormats/Label102x76Format1';
import Label102x76Format2 from './PrintFormats/Label102x76Format2';
import A4FourPerPageFormat from './PrintFormats/A4FourPerPageFormat';
import A4FourPerPageLandscapeFormat from './PrintFormats/A4FourPerPageLandscapeFormat';
import Label102x76Format3 from './PrintFormats/Label102x76Format3';
import Label102x76Format4 from './PrintFormats/Label102x76Format4';
import FormatSelectorModal from './components/FormatSelectorModal';
import styles from './PrintWaybillsPage.module.css';

const STORAGE_KEY = 'waybillPrintFormat';
const SORT_STORAGE_KEY = 'waybillSortOption';

const PrintWaybillsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printReady, setPrintReady] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [formatInitialized, setFormatInitialized] = useState(false);
  const [selectedSort, setSelectedSort] = useState(() => localStorage.getItem(SORT_STORAGE_KEY) || 'none');

  const packingBatchId = searchParams.get('packingBatchId');
  const orderIds = searchParams.get('orderIds');
  const formatParam = searchParams.get('format');

  // Add body class for print isolation — allows global CSS to hide everything
  // except the waybill print content when printing
  useEffect(() => {
    document.body.classList.add('printing-waybills');
    return () => {
      document.body.classList.remove('printing-waybills');
    };
  }, []);

  useEffect(() => {
    initializeFormat();
  }, []);

  useEffect(() => {
    // Only load data after format is initialized
    if (formatInitialized && selectedFormat) {
      loadPrintData();
    }
  }, [formatInitialized, selectedFormat]);

  useEffect(() => {
    if (!selectedFormat) return;

    const styleId = 'dynamic-page-size';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    if (selectedFormat === 'a4-four-landscape') {
      styleEl.textContent = `@media print { @page { size: A4 landscape !important; margin: 0 !important; } }`;
    } else if (selectedFormat === '102x76') {
      styleEl.textContent = `@media print { @page { size: 76mm 102mm !important; margin: 0 !important; } }`;
    } else if (selectedFormat === '102x76-h' || selectedFormat === '102x76-h-qr' || selectedFormat === '102x76-h-extended') {
      styleEl.textContent = `@media print { @page { size: 102mm 76mm !important; margin: 0 !important; } }`;
    } else {
      styleEl.textContent = `@media print { @page { size: A4 portrait !important; margin: 0 !important; } }`;
    }

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.textContent = '';
    };
  }, [selectedFormat]);

  const initializeFormat = () => {
    if (formatParam) {
      // Format provided in URL
      setSelectedFormat(formatParam);
      setFormatInitialized(true);
    } else {
      // Check localStorage
      const savedFormat = localStorage.getItem(STORAGE_KEY);
      if (savedFormat) {
        setSelectedFormat(savedFormat);
        setFormatInitialized(true);
      } else {
        // No format available, show modal
        setShowFormatModal(true);
        setLoading(false); // Stop loading to show modal
        setFormatInitialized(true);
      }
    }
  };

  const loadPrintData = async () => {
    try {
      setLoading(true);
      setPrintReady(false);

      // Validate parameters
      if (!packingBatchId && !orderIds) {
        showError('Either packing batch or order IDs must be provided');
        setLoading(false);
        return;
      }

      if (packingBatchId && orderIds) {
        showError('Cannot provide both packing batch and order IDs');
        setLoading(false);
        return;
      }

      const params = {};
      if (packingBatchId) {
        params.packingBatchId = packingBatchId;
      } else {
        params.orderIds = orderIds;
      }

      const data = await getPackingBatchPrintData(params);
      setPrintData(data);
      
      setTimeout(() => {
        setPrintReady(true);
      }, 500);
    } catch (error) {
      console.error('Failed to load print data:', error);
      showError(error.response?.data?.message || t('print.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFormatSelect = (format) => {
    setSelectedFormat(format);
    localStorage.setItem(STORAGE_KEY, format);
    
    // Update URL with selected format
    const newParams = new URLSearchParams(searchParams);
    newParams.set('format', format);
    setSearchParams(newParams);
    
    setShowFormatModal(false);
  };

  const handleFormatChange = (e) => {
    const newFormat = e.target.value;
    handleFormatSelect(newFormat);
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSelectedSort(newSort);
    localStorage.setItem(SORT_STORAGE_KEY, newSort);
  };

  const getSortedPrintData = () => {
    if (!printData || selectedSort === 'none') return printData;
    const sortedOrders = [...printData.orders].sort((a, b) => {
      switch (selectedSort) {
        case 'orderId':
          return a.orderId - b.orderId;
        case 'airwaybillNumber':
          return (a.airwaybillNumber || '').localeCompare(b.airwaybillNumber || '');
        case 'itemsDisplay': {
          const aItem = a.items?.[0]?.itemDisplayStr || '';
          const bItem = b.items?.[0]?.itemDisplayStr || '';
          return aItem.localeCompare(bItem);
        }
        default:
          return 0;
      }
    });
    return { ...printData, orders: sortedOrders };
  };

  const handlePrint = async () => {
    if (!printReady) {
      showError(t('print.waitForLoad'));
      return;
    }
    window.print();
    // Mark batch as printed if this is a batch print (not standalone order IDs)
    if (packingBatchId) {
      try {
        await markBatchAsPrinted(packingBatchId);
      } catch (err) {
        console.error('Failed to mark batch as printed:', err);
        // Non-blocking — do not show error to avoid interrupting the print flow
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderFormat = () => {
    if (!printData) return null;

    const sortedPrintData = getSortedPrintData();

    switch (selectedFormat) {
      case '102x76':
        return <Label102x76Format1 printData={sortedPrintData} />;
      case '102x76-h':
        return <Label102x76Format2 printData={sortedPrintData} />;
      case 'a4-four':
        return <A4FourPerPageFormat printData={sortedPrintData} />;
      case 'a4-four-landscape':
        return <A4FourPerPageLandscapeFormat printData={sortedPrintData} />;
      case '102x76-h-qr':
        return <Label102x76Format3 printData={sortedPrintData} />;
      case '102x76-h-extended':
        return <Label102x76Format4 printData={sortedPrintData} />;
      default:
        return (
          <div className={styles.errorContainer}>
            <AlertCircle size={48} />
            <h3>{t('print.invalidFormat')}</h3>
            <p>{t('print.formatNotSupported', { format: selectedFormat })}</p>
          </div>
        );
    }
  };

  const getInfoDisplay = () => {
    if (!printData) return '';

    const { batch, orders } = printData;
    const parts = [];

    // Batch number or "Selected Orders"
    if (batch.batchNumber) {
      parts.push(`Batch #${batch.batchNumber}`);
    } else {
      parts.push(t('print.selectedOrdersLabel'));
    }

    // Label count
    parts.push(t('print.labelsCount', { count: orders.length }));

    // Courier info
    if (batch.courier && batch.account) {
      parts.push(`${batch.courier.name} - ${batch.account.name}`);
    } else if (batch.courier) {
      parts.push(batch.courier.name);
    } else {
      parts.push(t('print.mixedCouriers'));
    }

    return parts.join(' • ');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 size={48} className={styles.spinning} />
        <p>{t('print.loadingData')}</p>
        <p className={styles.loadingSubtext}>
          {t('print.preparingLabels')}
        </p>
      </div>
    );
  }

  if (!printData && !showFormatModal) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle size={48} />
        <h3>{t('print.noDataAvailable')}</h3>
        <p>{t('print.unableToLoad')}</p>
        <button onClick={handleBack} className={styles.backButton}>
          <ArrowLeft size={18} />
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.printPage}>
      {/* Format Selector Modal */}
      <FormatSelectorModal
        isOpen={showFormatModal}
        onClose={() => setShowFormatModal(false)}
        onSelectFormat={handleFormatSelect}
      />

      {/* Only show print controls if we have data */}
      {printData && (
        <>
          {/* Print Controls - Hidden when printing */}
          <div className={styles.printControls}>
            <div className={styles.controlsHeader}>
              <button onClick={handleBack} className={styles.backButton}>
                <ArrowLeft size={18} />
                {t('common.back')}
              </button>
              
              <div className={styles.batchInfo}>
                <h2>{t('print.printWaybills')}</h2>
                <div className={styles.batchDetails}>
                  {getInfoDisplay()}
                </div>
              </div>

              <button 
                onClick={handlePrint} 
                className={styles.printButton}
                disabled={!printReady}
              >
                {printReady ? (
                  <>
                    <Printer size={18} />
                    {t('print.printLabels')}
                  </>
                ) : (
                  <>
                    <Loader2 size={18} className={styles.spinning} />
                    {t('print.preparing')}
                  </>
                )}
              </button>
            </div>

            <div className={styles.formatInfo}>
              {/* Format Selector Dropdown */}
              <div className={styles.infoCard}>
                <strong>{t('print.format')}:</strong>
                <select
                  value={selectedFormat}
                  onChange={handleFormatChange}
                  className={styles.formatSelect}
                >
                  <option value="102x76">{t('print.labelFormatVertical')}</option>
                  <option value="102x76-h">{t('print.labelFormatHorizontal')}</option>
                  <option value="a4-four">{t('print.a4Format')}</option>
                  <option value="a4-four-landscape">{t('print.a4FormatLandscape')}</option>
                  <option value="102x76-h-qr">{t('print.labelFormatHorizontalQr')}</option>
                  <option value="102x76-h-extended">{t('print.labelFormatHorizontalExtended')}</option>
                </select>
              </div>

              {/* Sort Selector Dropdown */}
              <div className={styles.infoCard}>
                <strong>{t('print.sortBy')}:</strong>
                <select
                  value={selectedSort}
                  onChange={handleSortChange}
                  className={styles.formatSelect}
                >
                  <option value="none">{t('print.sortDoNotSort')}</option>
                  <option value="itemsDisplay">{t('print.sortItemsDisplay')}</option>
                  <option value="orderId">{t('print.sortOrderId')}</option>
                  <option value="airwaybillNumber">{t('print.sortAirwaybillNumber')}</option>
                </select>
              </div>

              {printData.batch.warehouse && (
                <div className={styles.infoCard}>
                  <strong>{t('print.warehouse')}:</strong>
                  <span>{printData.batch.warehouse.name}</span>
                </div>
              )}

              {printData.batch.account && (
                <div className={styles.infoCard}>
                  <strong>{t('print.courierAccount')}:</strong>
                  <span>{printData.batch.account.name}</span>
                </div>
              )}

              {printReady && (
                <div className={`${styles.infoCard} ${styles.readyCard}`}>
                  <CheckCircle size={16} />
                  <span>{t('print.readyToPrint')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Print Content */}
          <div className={`${styles.printContent} waybill-print-content`}>
            {renderFormat()}
          </div>
        </>
      )}
    </div>
  );
};

export default PrintWaybillsPage;